import { Link as RouterLink } from 'react-router-dom';
import { Box, Text } from '@primer/react';
import { ArrowRightIcon } from '@primer/octicons-react';
import Reveal from './Reveal';
import HeroBackground from './HeroBackground';
import { CALENDLY_URL, trackContactClick } from '../utils/links';

function ServiceHero({
  headline,
  subtitle,
  videoSrc,
  videoLoop = true,
  videoPoster,
  secondaryCtaLabel = 'View Services',
  secondaryCtaHref = '#services',
}) {
  const isInternalRoute = secondaryCtaHref.startsWith('/');
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '75vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'canvas.default',
        paddingX: ['24px', '48px', '100px'],
        paddingTop: ['100px', '80px', '60px'],
        paddingBottom: [7, 7, 0],
        overflow: 'hidden',
      }}
    >
      {videoSrc && <HeroBackground videoSrc={videoSrc} poster={videoPoster} loop={videoLoop} />}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: videoSrc ? 'rgba(5, 7, 10, 0.55)' : undefined,
          backgroundImage: videoSrc
            ? undefined
            : 'radial-gradient(circle at 20% 20%, rgba(96, 165, 250, 0.16), transparent 55%), radial-gradient(circle at 80% 60%, rgba(167, 139, 250, 0.14), transparent 55%)',
        }}
      />

      <Box sx={{ position: 'relative', width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
        <Box sx={{ maxWidth: '900px' }}>
        <Reveal delay={0.1}>
          <Box as="h1" sx={{ margin: 0, marginBottom: 4 }}>
            {headline.map((line) => (
              <Text
                key={line.text}
                as="span"
                sx={{
                  display: 'block',
                  margin: 0,
                  fontFamily: "'Fraunces', serif",
                  fontStyle: line.italic ? 'italic' : 'normal',
                  fontWeight: line.tone === 'gradient' ? 500 : 300,
                  fontSize: ['2.25rem', '3.25rem', '4rem'],
                  lineHeight: 1.15,
                  color: line.tone === 'plain' ? 'fg.default' : undefined,
                  backgroundImage: line.tone === 'gradient' ? 'linear-gradient(90deg, #8ec5ff, #c4b5fd)' : undefined,
                  backgroundClip: line.tone === 'gradient' ? 'text' : undefined,
                  WebkitBackgroundClip: line.tone === 'gradient' ? 'text' : undefined,
                  WebkitTextFillColor: line.tone === 'gradient' ? 'transparent' : undefined,
                  ...(line.tone === 'muted' && { color: 'fg.muted' }),
                }}
              >
                {line.text}
              </Text>
            ))}
          </Box>
        </Reveal>

        <Reveal delay={0.2}>
          <Text
            as="p"
            sx={{
              margin: 0,
              marginBottom: 5,
              maxWidth: '560px',
              fontSize: 2,
              lineHeight: 1.7,
              color: 'fg.muted',
            }}
          >
            {subtitle}
          </Text>
        </Reveal>

        <Reveal delay={0.3}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box
              as="a"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackContactClick}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                paddingX: 4,
                paddingY: 2,
                borderRadius: '6px',
                border: '1px solid rgba(27, 31, 36, 0.15)',
                backgroundColor: '#1f883d',
                color: '#ffffff',
                fontSize: 1,
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'background-color 0.15s ease',
                ':hover': { backgroundColor: '#1c8139' },
                ':active': { backgroundColor: '#197935' },
              }}
            >
              Schedule a Consultation
              <ArrowRightIcon size={16} />
            </Box>

            <Box
              as={isInternalRoute ? RouterLink : 'a'}
              {...(isInternalRoute ? { to: secondaryCtaHref } : { href: secondaryCtaHref })}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                paddingX: 4,
                paddingY: 2,
                borderRadius: '6px',
                border: '1px solid',
                borderColor: 'border.default',
                color: 'fg.default',
                fontSize: 1,
                fontWeight: 500,
                textDecoration: 'none',
                ':hover': { backgroundColor: 'neutral.subtle' },
              }}
            >
              {secondaryCtaLabel}
            </Box>
          </Box>
        </Reveal>
        </Box>
      </Box>
    </Box>
  );
}

export default ServiceHero;
